/**
 * Agenda — functional appointment scheduling (adherence loop Fase 3).
 *
 * Nutritionist view: upcoming appointments grouped by day, a "Nueva cita"
 * dialog (pick patient, date/time, duration, notes), and per-appointment
 * actions: confirm, cancel, add to Google Calendar, download .ics.
 *
 * Patients without an OAuth calendar sync still get a one-click
 * "add to Google Calendar" link (Avena lesson: integrate > build).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider, IconButton, MenuItem,
  Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CalendarMonth as CalendarMonthIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import appointmentsService from '../../services/appointmentsService';
import patientsService from '../../services/patientsService';
import { API_BASE_URL } from '../../config/api';

const GREEN = '#006847';

const STATUS_META = {
  scheduled: { label: 'Agendada', color: '#2196F3', bg: '#e8f1fb' },
  confirmed: { label: 'Confirmada', color: GREEN, bg: '#e8f3ee' },
  cancelled: { label: 'Cancelada', color: '#9e9e9e', bg: '#f0f0f0' },
  completed: { label: 'Completada', color: '#6a4caf', bg: '#efeaf7' },
  no_show: { label: 'No asistió', color: '#c62828', bg: '#fbe9e9' },
};

const TYPES = ['consulta', 'seguimiento', 'primera vez', 'control', 'videollamada'];

const dayKey = (iso) => {
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
};
const timeOf = (iso) => {
  const d = new Date(iso.endsWith('Z') ? iso : `${iso}Z`);
  return d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
};

// datetime-local default = next round hour
const defaultWhen = () => {
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const AgendaScreen = () => {
  const [appts, setAppts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    patientUserId: '', when: defaultWhen(), durationMinutes: 30,
    type: 'consulta', notes: '', location: '',
  });

  const load = useCallback(async () => {
    try {
      setError('');
      const data = await appointmentsService.list();
      setAppts(data);
    } catch (e) {
      setError('No se pudo cargar la agenda.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    patientsService.getPatients().then((ps) => setPatients(ps || [])).catch(() => {});
  }, []);

  const grouped = useMemo(() => {
    const upcoming = appts
      .filter((a) => a.status !== 'cancelled')
      .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
    const map = new Map();
    for (const a of upcoming) {
      const k = dayKey(a.scheduled_at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(a);
    }
    return Array.from(map.entries());
  }, [appts]);

  const submit = async () => {
    if (!form.patientUserId || !form.when) return;
    setSubmitting(true);
    setError('');
    try {
      // datetime-local is local time; send as ISO (UTC).
      const scheduledAt = new Date(form.when).toISOString();
      await appointmentsService.create({
        patientUserId: Number(form.patientUserId),
        scheduledAt,
        durationMinutes: Number(form.durationMinutes),
        type: form.type,
        notes: form.notes,
        location: form.location,
      });
      setOpen(false);
      setForm((f) => ({ ...f, notes: '', location: '' }));
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo crear la cita.');
    } finally {
      setSubmitting(false);
    }
  };

  const doConfirm = async (a) => {
    try { const u = await appointmentsService.confirm(a.id); replace(u); }
    catch { setError('No se pudo confirmar.'); }
  };
  const doCancel = async (a) => {
    try { await appointmentsService.cancel(a.id); setAppts((p) => p.map((x) => x.id === a.id ? { ...x, status: 'cancelled' } : x)); }
    catch { setError('No se pudo cancelar.'); }
  };
  const replace = (u) => setAppts((p) => p.map((x) => (x.id === u.id ? u : x)));

  const downloadIcs = async (a) => {
    try {
      const resp = await fetch(appointmentsService.icsUrl(a.id), { credentials: 'include' });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cita-${a.id}.ics`;
      link.click();
      URL.revokeObjectURL(url);
    } catch { setError('No se pudo descargar el .ics'); }
  };

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: GREEN }}>
            <CalendarMonthIcon sx={{ mb: '-4px', mr: 1 }} />Agenda
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tus citas con pacientes. Recordatorio automático 24h antes.
          </Typography>
        </Box>
        <Button
          variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}
          sx={{ bgcolor: GREEN, borderRadius: 3, fontWeight: 700, '&:hover': { bgcolor: '#005035' } }}
        >
          Nueva cita
        </Button>
      </Stack>

      {error && <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: GREEN }} /></Box>
      ) : grouped.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6, bgcolor: '#f3f7f4', borderRadius: 3, px: 3 }}>
          <EventIcon sx={{ fontSize: 48, color: GREEN, mb: 1 }} />
          <Typography variant="h6" sx={{ color: GREEN }}>No tienes citas próximas</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Crea tu primera cita con el botón "Nueva cita".
          </Typography>
        </Box>
      ) : (
        <Stack spacing={3}>
          {grouped.map(([day, items]) => (
            <Box key={day}>
              <Typography variant="subtitle2" sx={{ color: GREEN, textTransform: 'capitalize', mb: 1, fontWeight: 700 }}>
                {day}
              </Typography>
              <Stack spacing={1.5}>
                {items.map((a) => {
                  const sm = STATUS_META[a.status] || STATUS_META.scheduled;
                  return (
                    <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 2, p: 2,
                        border: '1px solid #e6ece8', borderRadius: 3, bgcolor: 'white',
                      }}>
                        <Box sx={{ textAlign: 'center', minWidth: 64 }}>
                          <Typography variant="h6" fontWeight={700} sx={{ color: '#2c3e50' }}>
                            {timeOf(a.scheduled_at)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {a.duration_minutes} min
                          </Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle1" fontWeight={600} noWrap>
                            {a.patient_name || `Paciente #${a.patient_user_id}`}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.3 }} flexWrap="wrap">
                            <Chip size="small" label={a.appointment_type}
                              sx={{ bgcolor: '#eef2ef', color: '#446', textTransform: 'capitalize' }} />
                            <Chip size="small" label={sm.label} sx={{ bgcolor: sm.bg, color: sm.color, fontWeight: 600 }} />
                            {a.reminder_sent && (
                              <Chip size="small" label="Recordado" variant="outlined"
                                sx={{ borderColor: '#dfe7e2', color: '#7a8a80' }} />
                            )}
                          </Stack>
                          {a.notes && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                              {a.notes}
                            </Typography>
                          )}
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          {a.status !== 'confirmed' && (
                            <Tooltip title="Marcar confirmada">
                              <IconButton size="small" onClick={() => doConfirm(a)} sx={{ color: GREEN }}>
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Agregar a Google Calendar">
                            <IconButton size="small" component="a" target="_blank" rel="noopener noreferrer"
                              href={appointmentsService.googleCalendarUrl(a)} sx={{ color: '#4285F4' }}>
                              <CalendarMonthIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Descargar .ics">
                            <IconButton size="small" onClick={() => downloadIcs(a)} sx={{ color: '#6a7' }}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancelar cita">
                            <IconButton size="small" onClick={() => doCancel(a)} sx={{ color: '#c62828' }}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Box>
                    </motion.div>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      {/* New appointment dialog */}
      <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700, color: GREEN }}>Nueva cita</DialogTitle>
        <DialogContent>
          <TextField
            select fullWidth size="small" margin="dense" label="Paciente"
            value={form.patientUserId}
            onChange={(e) => setForm({ ...form, patientUserId: e.target.value })}
            helperText={patients.length === 0 ? 'No tienes pacientes asignados aún' : ' '}
          >
            {patients.map((p) => (
              <MenuItem key={p.user_id || p.id} value={p.user_id}>
                {p.nombre_completo || p.name || `Paciente #${p.user_id}`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth size="small" margin="dense" label="Fecha y hora" type="datetime-local"
            InputLabelProps={{ shrink: true }} value={form.when}
            onChange={(e) => setForm({ ...form, when: e.target.value })}
          />
          <Stack direction="row" spacing={1}>
            <TextField
              select fullWidth size="small" margin="dense" label="Duración"
              value={form.durationMinutes}
              onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })}
            >
              {[15, 30, 45, 60, 90].map((m) => <MenuItem key={m} value={m}>{m} min</MenuItem>)}
            </TextField>
            <TextField
              select fullWidth size="small" margin="dense" label="Tipo"
              value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {TYPES.map((t) => <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>{t}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField
            fullWidth size="small" margin="dense" label="Lugar o link de videollamada (opcional)"
            value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
          <TextField
            fullWidth size="small" margin="dense" label="Notas (opcional)" multiline minRows={2}
            value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={submitting} sx={{ color: 'grey.600' }}>Cancelar</Button>
          <Button
            variant="contained" onClick={submit}
            disabled={submitting || !form.patientUserId || !form.when}
            sx={{ bgcolor: GREEN, fontWeight: 700, '&:hover': { bgcolor: '#005035' } }}
          >
            {submitting ? 'Guardando…' : 'Agendar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AgendaScreen;
